/* =========================================================
   XLSX export — hand-built OOXML.

   Why not SheetJS here: the community build cannot write cell
   styles, and the signed-off export spec requires a bold 16pt
   merged title row, a terracotta header band and thin borders
   on every cell. SheetJS *is* used for reading (see importXlsx),
   which is what the spec asked it for.

   Uses a "stored" (uncompressed) ZIP container, which Excel,
   LibreOffice and Numbers all open.
   ========================================================= */
import * as XLSX from 'xlsx';

const enc = new TextEncoder();

/* ---- CRC32 ---- */
const CRC_TABLE = (() => {
  const t = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    t[n] = c >>> 0;
  }
  return t;
})();

function crc32(bytes: Uint8Array): number {
  let c = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) c = CRC_TABLE[(c ^ bytes[i]) & 0xff] ^ (c >>> 8);
  return (c ^ 0xffffffff) >>> 0;
}

interface ZipEntry { name: string; data: Uint8Array }

function zip(files: ZipEntry[]): Uint8Array {
  const chunks: Uint8Array[] = [];
  const central: Uint8Array[] = [];
  let offset = 0;
  const u16 = (n: number) => [n & 0xff, (n >>> 8) & 0xff];
  const u32 = (n: number) => [n & 0xff, (n >>> 8) & 0xff, (n >>> 16) & 0xff, (n >>> 24) & 0xff];

  files.forEach((f) => {
    const nameBytes = enc.encode(f.name);
    const crc = crc32(f.data);
    const size = f.data.length;
    const local = ([] as number[]).concat(
      u32(0x04034b50), u16(20), u16(0), u16(0), u16(0), u16(0),
      u32(crc), u32(size), u32(size), u16(nameBytes.length), u16(0),
    );
    chunks.push(new Uint8Array(local), nameBytes, f.data);
    const localLen = local.length + nameBytes.length + size;

    central.push(
      new Uint8Array(
        ([] as number[]).concat(
          u32(0x02014b50), u16(20), u16(20), u16(0), u16(0), u16(0), u16(0),
          u32(crc), u32(size), u32(size), u16(nameBytes.length), u16(0), u16(0),
          u16(0), u16(0), u32(0), u32(offset),
        ),
      ),
      nameBytes,
    );
    offset += localLen;
  });

  const cdStart = offset;
  let cdSize = 0;
  central.forEach((c) => {
    chunks.push(c);
    cdSize += c.length;
  });

  const count = files.length;
  chunks.push(
    new Uint8Array(
      ([] as number[]).concat(
        u32(0x06054b50), u16(0), u16(0), u16(count), u16(count), u32(cdSize), u32(cdStart), u16(0),
      ),
    ),
  );

  const total = chunks.reduce((n, c) => n + c.length, 0);
  const out = new Uint8Array(total);
  let p = 0;
  chunks.forEach((c) => {
    out.set(c, p);
    p += c.length;
  });
  return out;
}

/* ---- helpers ---- */
const esc = (s: unknown) =>
  String(s ?? '').replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

function colLetter(n: number): string {
  let s = '';
  n++;
  while (n > 0) {
    const m = (n - 1) % 26;
    s = String.fromCharCode(65 + m) + s;
    n = Math.floor((n - 1) / 26);
  }
  return s;
}

const sanitizeSheet = (name?: string) =>
  (name || 'Sheet1').replace(/[\\/?*[\]:]/g, ' ').slice(0, 31) || 'Sheet1';

/* ---- styles: bold-16 title · header band · full borders ---- */
const stylesXml =
  `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
  `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
  `<fonts count="3">` +
  `<font><sz val="11"/><name val="Calibri"/></font>` +
  `<font><b/><sz val="16"/><color rgb="FF2A3441"/><name val="Calibri"/></font>` +
  `<font><b/><sz val="11"/><color rgb="FFFFFFFF"/><name val="Calibri"/></font>` +
  `</fonts>` +
  `<fills count="3">` +
  `<fill><patternFill patternType="none"/></fill>` +
  `<fill><patternFill patternType="gray125"/></fill>` +
  `<fill><patternFill patternType="solid"><fgColor rgb="FFD97757"/><bgColor indexed="64"/></patternFill></fill>` +
  `</fills>` +
  `<borders count="2">` +
  `<border><left/><right/><top/><bottom/><diagonal/></border>` +
  `<border><left style="thin"/><right style="thin"/><top style="thin"/><bottom style="thin"/><diagonal/></border>` +
  `</borders>` +
  `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
  `<cellXfs count="4">` +
  `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
  `<xf numFmtId="0" fontId="1" fillId="0" borderId="0" xfId="0" applyFont="1"/>` +
  `<xf numFmtId="0" fontId="2" fillId="2" borderId="1" xfId="0" applyFont="1" applyFill="1" applyBorder="1"/>` +
  `<xf numFmtId="0" fontId="0" fillId="0" borderId="1" xfId="0" applyBorder="1"/>` +
  `</cellXfs>` +
  `</styleSheet>`;

export type Row = (string | number | null | undefined)[];

export interface BuildOptions {
  /** Row 1: bold, size 16, merged across every column, followed by a blank row. */
  title?: string;
}

/**
 * Single-sheet workbook from an array of rows. The first row of `rows`
 * is treated as the header (terracotta fill + bold white); every data
 * cell gets a thin border on all four sides.
 */
export function build(rows: Row[], sheetName?: string, opts: BuildOptions = {}): Uint8Array {
  return buildWorkbook([{ name: sheetName, rows, ...opts }]);
}

/** Una hoja dentro del libro. */
export interface Hoja extends BuildOptions {
  name?: string;
  rows: Row[];
}

/** El XML de una hoja suelta. Vivía dentro de `build`; se sacó cuando la
 *  plantilla del alta masiva necesitó un libro de dos hojas —los datos y
 *  el catálogo de identificadores—, para no acabar con dos generadores
 *  de OOXML que se separan con el tiempo. */
function hojaXml({ rows, title }: Hoja): string {
  const colCount = rows.reduce((m, r) => Math.max(m, r.length), 1);
  const offset = title ? 2 : 0;

  const xmlRows: string[] = [];
  if (title) {
    xmlRows.push(
      `<row r="1" ht="24" customHeight="1"><c r="A1" s="1" t="inlineStr"><is><t xml:space="preserve">${esc(title)}</t></is></c></row>`,
    );
  }
  rows.forEach((row, ri) => {
    const rn = ri + 1 + offset;
    const s = ri === 0 ? 2 : 3; // header : bordered data
    const cells = row
      .map((val, ci) => {
        const ref = colLetter(ci) + rn;
        return `<c r="${ref}" s="${s}" t="inlineStr"><is><t xml:space="preserve">${esc(val)}</t></is></c>`;
      })
      .join('');
    xmlRows.push(`<row r="${rn}">${cells}</row>`);
  });

  const colsXml = `<cols><col min="1" max="${colCount}" width="22" customWidth="1"/></cols>`;
  const mergeXml =
    title && colCount > 1
      ? `<mergeCells count="1"><mergeCell ref="A1:${colLetter(colCount - 1)}1"/></mergeCells>`
      : '';

  return (
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">${colsXml}<sheetData>${xmlRows.join('')}</sheetData>${mergeXml}</worksheet>`
  );
}

/**
 * Libro de una o varias hojas. La primera fila de cada hoja es su
 * cabecera (banda terracota, negrita en blanco); las celdas de datos
 * llevan borde fino en los cuatro lados.
 */
export function buildWorkbook(hojas: Hoja[]): Uint8Array {
  const usadas: Hoja[] = hojas.length ? hojas : [{ rows: [] }];

  // Dos hojas con el mismo nombre hacen que Excel se niegue a abrir el
  // archivo, y el saneado puede colapsar dos nombres distintos en uno.
  const vistos = new Set<string>();
  const nombres = usadas.map((h, i) => {
    const base = sanitizeSheet(h.name ?? `Hoja${i + 1}`);
    let n = base;
    let k = 2;
    while (vistos.has(n.toLowerCase())) n = `${base} ${k++}`.slice(0, 31);
    vistos.add(n.toLowerCase());
    return n;
  });

  const sheetsXml = nombres
    .map((n, i) => `<sheet name="${esc(n)}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`)
    .join('');

  const workbook =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets>${sheetsXml}</sheets></workbook>`;

  // Los estilos toman el id siguiente al de la última hoja.
  const relsHojas = nombres
    .map((_, i) =>
      `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`)
    .join('');

  const wbRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    relsHojas +
    `<Relationship Id="rId${nombres.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/></Relationships>`;

  const rootRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/></Relationships>`;

  const overrides = nombres
    .map((_, i) =>
      `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`)
    .join('');

  const contentTypes =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>\n` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
    `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
    overrides +
    `</Types>`;

  return zip([
    { name: '[Content_Types].xml', data: enc.encode(contentTypes) },
    { name: '_rels/.rels', data: enc.encode(rootRels) },
    { name: 'xl/workbook.xml', data: enc.encode(workbook) },
    { name: 'xl/_rels/workbook.xml.rels', data: enc.encode(wbRels) },
    { name: 'xl/styles.xml', data: enc.encode(stylesXml) },
    ...usadas.map((h, i) => ({
      name: `xl/worksheets/sheet${i + 1}.xml`,
      data: enc.encode(hojaXml(h)),
    })),
  ]);
}

/** Descarga un libro de varias hojas. */
export function downloadSheets(filename: string, hojas: Hoja[]) {
  const bytes = buildWorkbook(hojas);
  const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  downloadBlob(
    /\.xlsx$/i.test(filename) ? filename : filename + '.xlsx',
    new Blob([ab], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
}

/** Trigger a browser download of a blob. */
export function downloadBlob(filename: string, blob: Blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }, 200);
}

export function download(filename: string, rows: Row[], sheetName?: string, opts?: BuildOptions) {
  const bytes = build(rows, sheetName, opts);
  const ab = bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength) as ArrayBuffer;
  downloadBlob(
    /\.xlsx$/i.test(filename) ? filename : filename + '.xlsx',
    new Blob([ab], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
  );
}

/** CSV export. Plain text carries no styling, so the title becomes line 1. */
export function downloadCsv(filename: string, rows: Row[], title?: string) {
  const cell = (v: unknown) => {
    const s = String(v ?? '');
    return /[",\n;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines: string[] = [];
  if (title) lines.push(cell(title), '');
  rows.forEach((r) => lines.push(r.map(cell).join(',')));
  downloadBlob(
    /\.csv$/i.test(filename) ? filename : filename + '.csv',
    // BOM so Excel picks up UTF-8 accents
    new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' }),
  );
}

/* ---------------- Import (SheetJS) ---------------- */

export interface ParsedSheet {
  /** Column headers, in file order. */
  headers: string[];
  /** One record per data row, keyed by header. */
  rows: Record<string, string>[];
  sheetName: string;
}

/** Parse an .xlsx / .xls / .csv file into headers + records. */
export async function parseSpreadsheet(file: File): Promise<ParsedSheet> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array' });
  const sheetName = wb.SheetNames[0];
  const sheet = wb.Sheets[sheetName];
  if (!sheet) return { headers: [], rows: [], sheetName: sheetName ?? '' };

  const matrix = XLSX.utils.sheet_to_json<string[]>(sheet, {
    header: 1,
    raw: false,
    defval: '',
    blankrows: false,
  });
  if (!matrix.length) return { headers: [], rows: [], sheetName };

  const headers = (matrix[0] || []).map((h) => String(h ?? '').trim());
  const rows = matrix.slice(1).map((r) => {
    const rec: Record<string, string> = {};
    headers.forEach((h, i) => {
      if (h) rec[h] = String(r?.[i] ?? '').trim();
    });
    return rec;
  });
  return { headers, rows, sheetName };
}
